import {useEffect} from 'react'

const mobileQuery = '(max-width: 899px), (hover: none) and (pointer: coarse)'

export function isKeyboardEditable(element: Element | null): element is HTMLElement {
  if (!(element instanceof HTMLElement)) return false
  if (element.isContentEditable) return true
  if (element instanceof HTMLTextAreaElement) return !element.readOnly && !element.disabled
  return element instanceof HTMLInputElement && !element.readOnly && !element.disabled &&
    ['text', 'search', 'email', 'password', 'tel', 'url', 'number'].includes(element.type)
}

export function keyboardIsOpen(editable: boolean, scale: number, baseline: number, height: number) {
  return editable && Math.abs(scale - 1) < 0.05 && baseline - height > Math.max(150, baseline * 0.2)
}

/** One owner for the visual viewport, including login and portal dialogs. Never changes zoom. */
export function useMobileViewport() {
  useEffect(() => {
    const root = document.documentElement
    const viewport = window.visualViewport
    const media = window.matchMedia(mobileQuery)
    const names = ['--visible-height', '--visible-top'] as const
    const previous = names.map(name => [name, root.style.getPropertyValue(name)] as const)
    const previousState = root.getAttribute('data-keyboard-open')
    let baseline = Math.max(window.innerHeight, viewport?.height ?? 0)
    let width = window.innerWidth
    let frame = 0
    let settle = 0
    let revealRequested = false

    const reveal = () => {
      const element = document.activeElement
      if (!isKeyboardEditable(element) || !viewport) return
      // Scroll only an actually obscured field, within its existing scroll owner.
      const rect = element.getBoundingClientRect()
      const dialog = element.closest('[role="dialog"]')
      const header = dialog?.querySelector('.modal-header')?.getBoundingClientRect()
      const footer = dialog?.querySelector('.modal-footer')?.getBoundingClientRect()
      const top = Math.max(viewport.offsetTop + 48, header?.bottom ?? 0)
      const bottom = Math.min(viewport.offsetTop + viewport.height - 56, footer?.top ?? Infinity)
      const delta = rect.bottom > bottom ? rect.bottom - bottom : rect.top < top ? rect.top - top : 0
      if (!delta) return
      let owner = element.parentElement
      while (owner && owner !== document.body) {
        const overflow = getComputedStyle(owner).overflowY
        if (/(auto|scroll)/.test(overflow) && owner.scrollHeight > owner.clientHeight) {
          owner.scrollTop += delta
          return
        }
        owner = owner.parentElement
      }
      if (!dialog) window.scrollBy(0, delta)
    }
    const update = () => {
      frame = 0
      const scale = viewport?.scale ?? 1
      const height = viewport?.height ?? window.innerHeight
      const editable = isKeyboardEditable(document.activeElement)
      if (Math.abs(window.innerWidth - width) > 80 && Math.abs(scale - 1) < 0.05) {
        width = window.innerWidth
        baseline = Math.max(window.innerHeight, document.documentElement.clientHeight, height)
      }
      if (!editable && Math.abs(scale - 1) < 0.05) baseline = Math.max(height, window.innerHeight)
      else baseline = Math.max(baseline, height)
      const open = media.matches && keyboardIsOpen(editable, scale, baseline, height)
      root.toggleAttribute('data-keyboard-open', open)
      if (media.matches && Math.abs(scale - 1) < 0.05) {
        root.style.setProperty('--visible-height', `${height}px`)
        root.style.setProperty('--visible-top', `${viewport?.offsetTop ?? 0}px`)
      } else {
        names.forEach(name => root.style.removeProperty(name))
      }
      if (open && revealRequested) reveal()
      revealRequested = false
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    const focusChanged = () => {
      revealRequested = true
      schedule()
      // Covers delayed Safari keyboard animation and a field-to-field focus transfer.
      window.clearTimeout(settle)
      settle = window.setTimeout(schedule, 350)
    }
    const resized = () => { revealRequested = true; schedule() }
    viewport?.addEventListener('resize', resized)
    viewport?.addEventListener('scroll', schedule)
    window.addEventListener('resize', resized)
    window.addEventListener('orientationchange', resized)
    media.addEventListener('change', schedule)
    document.addEventListener('focusin', focusChanged)
    document.addEventListener('focusout', focusChanged)
    schedule()
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(settle)
      viewport?.removeEventListener('resize', resized)
      viewport?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', resized)
      window.removeEventListener('orientationchange', resized)
      media.removeEventListener('change', schedule)
      document.removeEventListener('focusin', focusChanged)
      document.removeEventListener('focusout', focusChanged)
      previous.forEach(([name, value]) => value ? root.style.setProperty(name, value) : root.style.removeProperty(name))
      if (previousState === null) root.removeAttribute('data-keyboard-open')
      else root.setAttribute('data-keyboard-open', previousState)
    }
  }, [])
}
